import { useEffect, useState } from 'react';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import type { Tono } from '../componentes/Etiqueta';
import { DialogoMotivo } from '../componentes/DialogoMotivo';
import { diasHasta, laFecha, laFechaHora } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { ErrorApi } from '../dominio/clienteApi';
import {
  ESTADOS_VERIFICACION,
  aprobarDocumento,
  aprobarExpediente,
  obtenerCola,
  obtenerDetalle,
  obtenerUrlArchivo,
  rechazarDocumento,
  rechazarExpediente,
  revocarConsentimientoPorOperador,
} from '../dominio/verificacionApi';
import type {
  DocumentoReal,
  EstadoRevisionDocumento,
  EstadoVerificacionConductor,
  TipoDocumento,
} from '../dominio/verificacionApi';

/**
 * Etiquetas en castellano de los ENUMS REALES del backend. Deliberadamente NO reusan
 * `componentes/Etiqueta.tsx`: sus diccionarios están indexados por los tipos MOCK de
 * `dominio/contratos.ts`, que no coinciden con estos (ver la cabecera de `dominio/
 * verificacionApi.ts`). Mismas clases CSS (`etiqueta etiqueta--<tono>`), para que se vea igual.
 */
const ESTADO_VERIFICACION_TEXTO: Record<EstadoVerificacionConductor, [string, Tono]> = {
  PENDIENTE: ['Pendiente', 'neutro'],
  EN_REVISION: ['En revisión', 'atencion'],
  APROBADO: ['Aprobado', 'bueno'],
  RECHAZADO: ['Rechazado', 'malo'],
  SUSPENDIDO: ['Suspendido', 'malo'],
};

const ESTADO_REVISION_TEXTO: Record<EstadoRevisionDocumento, [string, Tono]> = {
  PENDIENTE: ['Pendiente', 'atencion'],
  APROBADO: ['Aprobado', 'bueno'],
  RECHAZADO: ['Rechazado', 'malo'],
};

const TIPO_DOCUMENTO_TEXTO: Record<TipoDocumento, string> = {
  LICENCIA_CONDUCIR: 'Licencia de conducir',
  SOAT: 'SOAT',
  TARJETA_PROPIEDAD: 'Tarjeta de propiedad',
  REVISION_TECNICA: 'Revisión técnica',
};

function EtiquetaReal({ texto, tono }: { texto: string; tono: Tono }) {
  return <span className={`etiqueta etiqueta--${tono}`}>{texto}</span>;
}

function EtiquetaVerificacion({ valor }: { valor: EstadoVerificacionConductor }) {
  const [texto, tono] = ESTADO_VERIFICACION_TEXTO[valor];
  return <EtiquetaReal texto={texto} tono={tono} />;
}

function EtiquetaRevision({ valor }: { valor: EstadoRevisionDocumento }) {
  const [texto, tono] = ESTADO_REVISION_TEXTO[valor];
  return <EtiquetaReal texto={texto} tono={tono} />;
}

/** El mensaje que ve el operador cuando una mutación falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

/** Qué diálogo de motivo está abierto. Un solo estado para los tres casos evita que dos se solapen. */
type DialogoAbierto =
  | { tipo: 'rechazar-documento'; documento: DocumentoReal }
  | { tipo: 'rechazar-expediente' }
  | { tipo: 'revocar-consentimiento' };

export function Verificacion() {
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoVerificacionConductor>('EN_REVISION');
  const [cola, recargarCola] = useCargaReal(() => obtenerCola(estadoFiltro), [estadoFiltro]);

  const [seleccionadoId, setSeleccionadoId] = useState<string | null>(null);
  const [detalle, recargarDetalle] = useCargaReal(
    () => (seleccionadoId ? obtenerDetalle(seleccionadoId) : Promise.resolve(null)),
    [seleccionadoId],
  );

  const [dialogo, setDialogo] = useState<DialogoAbierto | null>(null);
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const ahora = new Date();

  // Si lo seleccionado ya no está en la cola (cambió el filtro, o una acción lo sacó de este
  // estado), cae al primero de la lista nueva — mismo comportamiento que tenía la pantalla mock.
  // Depende sólo de `cola` a propósito: no de `seleccionadoId` (eso reiniciaría la selección en
  // cada click) ni de una lista de deps más ancha que no aportaría nada nuevo.
  useEffect(() => {
    if (cola.estado !== 'listo') return;
    const sigueAhi = cola.datos.some((c) => c.conductorId === seleccionadoId);
    if (!sigueAhi) setSeleccionadoId(cola.datos[0]?.conductorId ?? null);
  }, [cola]);

  /** Ejecuta una mutación, recarga la cola y el detalle, y muestra el error si falla. */
  async function ejecutar(clave: string, accion: () => Promise<unknown>): Promise<void> {
    setAccionEnCurso(clave);
    setErrorAccion(null);
    try {
      await accion();
      recargarCola();
      recargarDetalle();
      setDialogo(null);
    } catch (error) {
      setErrorAccion(mensajeDeFalla(error));
    } finally {
      setAccionEnCurso(null);
    }
  }

  async function verArchivo(documento: DocumentoReal): Promise<void> {
    setAccionEnCurso(`archivo:${documento.id}`);
    setErrorAccion(null);
    try {
      const { url } = await obtenerUrlArchivo(documento.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setErrorAccion(mensajeDeFalla(error));
    } finally {
      setAccionEnCurso(null);
    }
  }

  if (cola.estado === 'cargando') {
    return (
      <Pagina rotulo="Conductores" titulo="Cola de verificación">
        <div className="bloque">
          <Cargando filas={5} etiqueta="Cargando la cola" />
        </div>
      </Pagina>
    );
  }

  if (cola.estado === 'error') {
    return (
      <Pagina rotulo="Conductores" titulo="Cola de verificación">
        <div className="bloque">
          <ConError detalle={cola.mensaje} alReintentar={recargarCola} />
        </div>
      </Pagina>
    );
  }

  const listaCola = cola.datos;
  const seleccionado =
    detalle.estado === 'listo' && detalle.datos && detalle.datos.conductor.id === seleccionadoId
      ? detalle.datos
      : null;

  return (
    <Pagina
      rotulo="Conductores"
      titulo="Cola de verificación"
      ayuda="Expedientes por estado. Un conductor no entra al reparto hasta tener todos sus documentos aprobados y vigentes, y el consentimiento de operación vigente."
      acciones={
        <label className="campo" style={{ minWidth: 220 }}>
          <span className="campo__etiqueta rotulo">Estado</span>
          <select
            className="selector"
            value={estadoFiltro}
            onChange={(evento) =>
              setEstadoFiltro(evento.target.value as EstadoVerificacionConductor)
            }
          >
            {ESTADOS_VERIFICACION.map((estado) => (
              <option key={estado} value={estado}>
                {ESTADO_VERIFICACION_TEXTO[estado][0]}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {errorAccion ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError titulo="La acción no se pudo completar" detalle={errorAccion} />
        </div>
      ) : null}

      {listaCola.length === 0 ? (
        <div className="bloque">
          <Vacio
            titulo="No hay expedientes en este estado"
            detalle="Cuando un conductor tenga expedientes en este estado, van a aparecer acá."
          />
        </div>
      ) : (
        <div
          className="rejilla"
          style={{ gridTemplateColumns: 'minmax(320px, 1fr) minmax(340px, 1.2fr)' }}
        >
          <Bloque titulo={`En este estado (${listaCola.length})`} sinRelleno>
            <Tabla
              cabeceras={['Conductor', 'Vehículo', 'Estado', { texto: 'Pendientes', num: true }]}
            >
              {listaCola.map((item) => {
                const elegido = seleccionadoId === item.conductorId;
                const conAlgoQueMirar = item.documentosVencidos.length > 0;
                return (
                  <tr
                    key={item.conductorId}
                    data-estado={conAlgoQueMirar ? 'atencion' : undefined}
                    style={elegido ? { background: 'var(--tinta-3)' } : undefined}
                  >
                    <td>
                      <button
                        type="button"
                        className="boton boton--chico"
                        style={{ border: 0, background: 'none', padding: 0, textAlign: 'left' }}
                        onClick={() => setSeleccionadoId(item.conductorId)}
                        aria-pressed={elegido}
                      >
                        {item.nombre ?? <span className="apagado">Sin nombre</span>}
                      </button>
                      <div className="apagado">
                        actualizado {laFechaHora(item.actualizadoAt)}
                        {!item.consentimientoOperacionVigente
                          ? ' · sin consentimiento vigente'
                          : ''}
                      </div>
                    </td>
                    <td>{item.placa ?? <span className="apagado">sin vehículo</span>}</td>
                    <td>
                      <EtiquetaVerificacion valor={item.estadoVerificacion} />
                    </td>
                    <td className="tabla__num">{item.documentosPendientes}</td>
                  </tr>
                );
              })}
            </Tabla>
          </Bloque>

          {seleccionadoId === null ? null : (
            <Bloque
              titulo={
                detalle.estado === 'listo' && seleccionado
                  ? (seleccionado.conductor.user.nombre ?? 'Sin nombre')
                  : 'Expediente'
              }
              nota="Cada documento se aprueba o rechaza por separado. Rechazar exige escribir qué le falta."
              acciones={
                seleccionado ? (
                  <span className="fila">
                    <button
                      type="button"
                      className="boton boton--chico"
                      disabled={accionEnCurso !== null}
                      onClick={() =>
                        ejecutar('aprobar-expediente', () =>
                          aprobarExpediente(seleccionado.conductor.id),
                        )
                      }
                    >
                      Aprobar expediente
                    </button>
                    <button
                      type="button"
                      className="boton boton--chico boton--peligro"
                      disabled={accionEnCurso !== null}
                      onClick={() => setDialogo({ tipo: 'rechazar-expediente' })}
                    >
                      Rechazar expediente
                    </button>
                  </span>
                ) : null
              }
            >
              {detalle.estado === 'cargando' ? (
                <Cargando filas={4} etiqueta="Cargando el expediente" />
              ) : detalle.estado === 'error' ? (
                <ConError detalle={detalle.mensaje} alReintentar={recargarDetalle} />
              ) : !seleccionado ? null : (
                <>
                  <dl className="datos" style={{ marginBottom: 14 }}>
                    <dt style={{ color: '#6a6154' }}>Correo</dt>
                    <dd>{seleccionado.conductor.user.email ?? '—'}</dd>
                    <dt style={{ color: '#6a6154' }}>Teléfono</dt>
                    <dd>{seleccionado.conductor.user.phone ?? '—'}</dd>
                    <dt style={{ color: '#6a6154' }}>Vehículo</dt>
                    <dd>
                      {seleccionado.conductor.vehiculo
                        ? `${seleccionado.conductor.vehiculo.placa} · ${
                            seleccionado.conductor.vehiculo.marca ?? ''
                          } ${seleccionado.conductor.vehiculo.modelo ?? ''}`.trim()
                        : 'sin vehículo'}
                    </dd>
                    <dt style={{ color: '#6a6154' }}>Consentimiento OPERACION_VIAJE</dt>
                    <dd>
                      {seleccionado.consentimientos.find((c) => c.finalidad === 'OPERACION_VIAJE')
                        ?.cubreTextoVigente ? (
                        <span className="fila" style={{ gap: 8 }}>
                          <EtiquetaReal texto="Vigente" tono="bueno" />
                          <button
                            type="button"
                            className="boton boton--chico"
                            disabled={accionEnCurso !== null}
                            onClick={() => setDialogo({ tipo: 'revocar-consentimiento' })}
                          >
                            Revocar
                          </button>
                        </span>
                      ) : (
                        <EtiquetaReal texto="No vigente" tono="malo" />
                      )}
                    </dd>
                  </dl>

                  <div className="pila">
                    {seleccionado.conductor.documentos.map((documento) => {
                      const dias = diasHasta(documento.vigenteHasta, ahora);
                      const vencido = dias !== null && dias < 0;
                      const clave = `doc:${documento.id}`;
                      const ocupado =
                        accionEnCurso === clave || accionEnCurso === `archivo:${documento.id}`;
                      return (
                        <div className="documento" key={documento.id}>
                          <div>
                            <div className="fila" style={{ justifyContent: 'space-between' }}>
                              <strong>{TIPO_DOCUMENTO_TEXTO[documento.tipo]}</strong>
                              <EtiquetaRevision valor={documento.estadoRevision} />
                            </div>
                            <dl className="datos" style={{ marginTop: 10, color: 'inherit' }}>
                              <dt style={{ color: '#6a6154' }}>Número</dt>
                              <dd>{documento.numero}</dd>
                              <dt style={{ color: '#6a6154' }}>Vigente hasta</dt>
                              <dd>
                                {laFecha(documento.vigenteHasta)}{' '}
                                {vencido ? (
                                  <strong style={{ color: '#a1332a' }}>
                                    vencido hace {Math.abs(dias)} días
                                  </strong>
                                ) : dias !== null && dias < 30 ? (
                                  <span style={{ color: '#8a5a11' }}>vence en {dias} días</span>
                                ) : null}
                              </dd>
                            </dl>
                            {documento.observacion ? (
                              <p style={{ marginTop: 10, color: '#8a3a30' }}>
                                {documento.observacion}
                              </p>
                            ) : null}
                          </div>

                          <div className="documento__pie">
                            <div className="fila" style={{ justifyContent: 'space-between' }}>
                              <span>
                                {documento.archivoRef ? (
                                  documento.archivoRef
                                ) : (
                                  <span className="apagado">sin archivo cargado</span>
                                )}
                              </span>
                              <span className="fila">
                                <button
                                  type="button"
                                  className="boton boton--chico"
                                  disabled={!documento.archivoRef || ocupado}
                                  onClick={() => verArchivo(documento)}
                                >
                                  Ver archivo
                                </button>
                                <button
                                  type="button"
                                  className="boton boton--chico"
                                  disabled={ocupado || documento.estadoRevision === 'APROBADO'}
                                  onClick={() =>
                                    ejecutar(clave, () => aprobarDocumento(documento.id))
                                  }
                                >
                                  Aprobar
                                </button>
                                <button
                                  type="button"
                                  className="boton boton--chico"
                                  disabled={ocupado}
                                  onClick={() =>
                                    setDialogo({ tipo: 'rechazar-documento', documento })
                                  }
                                >
                                  Rechazar
                                </button>
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Bloque>
          )}
        </div>
      )}

      {dialogo?.tipo === 'rechazar-documento' ? (
        <DialogoMotivo
          titulo={`Rechazar ${TIPO_DOCUMENTO_TEXTO[dialogo.documento.tipo]}`}
          descripcion="El conductor va a leer esto tal cual para saber qué corregir. Sé concreto: qué documento, qué le falta."
          etiquetaConfirmar="Rechazar documento"
          peligroso
          alConfirmar={(motivo) =>
            ejecutar(`doc:${dialogo.documento.id}`, () =>
              rechazarDocumento(dialogo.documento.id, motivo),
            )
          }
          alCancelar={() => setDialogo(null)}
        />
      ) : null}

      {dialogo?.tipo === 'rechazar-expediente' && seleccionado ? (
        <DialogoMotivo
          titulo="Rechazar el expediente completo"
          descripcion="Se rechazan todos los documentos pendientes con este motivo, y el conductor vuelve a EN_REVISION recién cuando corrija y vuelva a subir lo que falte."
          etiquetaConfirmar="Rechazar expediente"
          peligroso
          alConfirmar={(motivo) =>
            ejecutar('rechazar-expediente', () =>
              rechazarExpediente(seleccionado.conductor.id, motivo),
            )
          }
          alCancelar={() => setDialogo(null)}
        />
      ) : null}

      {dialogo?.tipo === 'revocar-consentimiento' && seleccionado ? (
        <DialogoMotivo
          titulo="Revocar el consentimiento de OPERACION_VIAJE"
          descripcion="El conductor sale del pool en el momento: deja de recibir ofertas hasta que vuelva a consentir. Usalo para un pedido del propio conductor por teléfono, o una baja administrativa."
          etiquetaConfirmar="Revocar consentimiento"
          peligroso
          alConfirmar={(motivo) =>
            ejecutar('revocar-consentimiento', () =>
              revocarConsentimientoPorOperador(seleccionado.conductor.id, motivo),
            )
          }
          alCancelar={() => setDialogo(null)}
        />
      ) : null}
    </Pagina>
  );
}
